import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import ScoreCard from "@/charts/ScoreCard";
import SingleColumnTable from "@/charts/SingleColumnTable";
import ThreeColumnTable from "@/charts/ThreeColumnTable";
import DonutChart from "@/charts/DonutChart";
import { ReactMarkdownWithGFMPlugin } from "@/utils";
import { SquareArrowOutUpRight, Eye } from "lucide-react";
import ToggleInput from "@/components/ui/ToggleInput";

function LLMResponseModal({ showModal, modalContent, onClose }) {
  const { t } = useTranslation();
  if (!showModal || !modalContent) return null;

  // Escape special regex chars in snippet text
  const escapeRegex = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  // Wrap snippet occurrences in <mark>
  const highlightSnippet = (response, snippet) => {
    if (!snippet || !response) return response;
    try {
      const pattern = new RegExp(escapeRegex(snippet.trim()), "gi");
      return response.replace(
        pattern,
        (match) => `<mark style="background-color: yellow;">${match}</mark>`,
      );
    } catch (e) {
      console.warn("Highlight error:", e);
      return response;
    }
  };

  const highlightedResponse = highlightSnippet(
    modalContent.response,
    modalContent.snippetText,
  );

  return (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      onClick={onClose}
    >
      <div
        className="modal-dialog modal-lg modal-dialog-scrollable"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content">
          <div
            className="modal-header"
            style={{ backgroundColor: "var(--bg-surface)" }}
          >
            <div>
              <h5 className="modal-title mb-1">
                {t("promptResearch.fullLlmResponse", "Full LLM Response")}
              </h5>
              <small className="text-muted text-capitalize">
                {t("promptResearch.provider", "Provider:")}{" "}
                <strong>
                  {modalContent.provider === "chatgpt"
                    ? "ChatGPT"
                    : modalContent.provider === "gemini"
                      ? "Gemini"
                      : modalContent.provider === "perplexity"
                        ? "Perplexity"
                        : modalContent.provider === "claude"
                          ? "Claude"
                          : modalContent.provider.charAt(0).toUpperCase() +
                            modalContent.provider.slice(1)}
                </strong>
              </small>
            </div>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              aria-label={t("common.close", "Close")}
            ></button>
          </div>
          <div className="modal-body">
            <div className="mb-3 p-3 bg-light rounded">
              <h6
                className="fw-bold mb-2"
                style={{ color: "var(--brand-color-dark)" }}
              >
                {t("promptResearch.relatedSnippet", "Related Snippet:")}
              </h6>
              <div className="mb-0 small text-muted">
                <ReactMarkdownWithGFMPlugin>
                  {modalContent.snippetText}
                </ReactMarkdownWithGFMPlugin>
              </div>
            </div>

            {/* Highlighted LLM response */}
            <div className="markdown-content">
              <div
                className="bg-white p-3 border card-radius"
                style={{ fontSize: "14px", lineHeight: "1.5" }}
              >
                <ReactMarkdownWithGFMPlugin>
                  {highlightedResponse}
                </ReactMarkdownWithGFMPlugin>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              {t("common.close", "Close")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function QueryResearchResult({ filteredQueryResearchData }) {
  const { t } = useTranslation();
  const [isOnlyBusinessCompetitor, setIsOnlyBusinessCompetitor] =
    useState(false);
  const [isOwnBrandOnly, setIsOwnBrandOnly] = useState(false);
  const [isOwnBrandMentionsOnly, setIsOwnBrandMentionsOnly] = useState(false);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState(null);

  // Function to close modal
  const closeModal = useCallback(() => {
    setShowModal(false);
    setModalContent(null);
  }, []);

  // Function to open modal with LLM response - wrapped with useCallback
  const openModal = useCallback(
    (snippetText) => {
      // Find the LLM response that contains this snippet
      const response = filteredQueryResearchData?.data?.llm_responses?.find(
        (response) => {
          return response.mention_stats.some((mentionStat) =>
            mentionStat.snippets?.some(
              (snippet) => snippet.text === snippetText,
            ),
          );
        },
      );

      if (response) {
        setModalContent({
          provider: response.llm_provider,
          response: response.response,
          snippetText: snippetText,
        });
        setShowModal(true);
      }
    },
    [filteredQueryResearchData],
  );

  // 1. Total Citation of Client's Brand (TotalCitation)
  const totalClientCitation =
    filteredQueryResearchData?.data?.llm_responses?.reduce(
      (total, response) => {
        return (
          total +
          response.citation_stats
            .filter((citation) => citation.is_own_brand)
            .reduce((sum, citation) => sum + citation.citation_count, 0)
        );
      },
      0,
    ) || 0;

  // 2. Share of Citations (SOC) - % of citation of brand
  const totalAllCitations =
    filteredQueryResearchData?.data?.llm_responses?.reduce(
      (total, response) => {
        return (
          total +
          response.citation_stats.reduce(
            (sum, citation) => sum + citation.citation_count,
            0,
          )
        );
      },
      0,
    ) || 0;

  const shareOfCitations =
    totalAllCitations > 0
      ? ((totalClientCitation / totalAllCitations) * 100).toFixed(2)
      : 0;

  // 3. Total Mention of Client's Brand (TotalMention)
  const totalClientMention =
    filteredQueryResearchData?.data?.llm_responses?.reduce(
      (total, response) => {
        const ownBrandSum = response.mention_stats.reduce((sum, mention) => {
          return sum + (mention.is_own_brand ? mention.mention_count || 0 : 0);
        }, 0);
        return total + ownBrandSum;
      },
      0,
    ) || 0;

  // 4. Share of Mentions (SOM) - % of mention of brand
  const totalAllMentions =
    filteredQueryResearchData?.data?.llm_responses?.reduce(
      (total, response) => {
        return (
          total +
          response.mention_stats.reduce((mentionTotal, mention) => {
            return mentionTotal + mention.mention_count;
          }, 0)
        );
      },
      0,
    ) || 0;

  const shareOfMentions =
    totalAllMentions > 0
      ? ((totalClientMention / totalAllMentions) * 100).toFixed(2)
      : 0;

  // 5. Brand Citation Value Count - A list of json {brand, count}
  const BrandCitationValueCount = React.useMemo(() => {
    if (!filteredQueryResearchData) return [];

    const brandCounts = {};

    filteredQueryResearchData.data.llm_responses.forEach((response) => {
      response.citation_stats.forEach((citation) => {
        const brand_name = citation.brand_name;
        if (brandCounts[brand_name]) {
          brandCounts[brand_name].count += citation.citation_count;
        } else {
          brandCounts[brand_name] = {
            count: citation.citation_count,
            is_own_brand: citation.is_own_brand,
            is_business_competitor: citation.is_business_competitor,
            brand_name: citation.brand_name,
          };
        }
      });
    });

    return Object.values(brandCounts).sort((a, b) => b.count - a.count);
  }, [filteredQueryResearchData]);

  const filteredBrandCitationValueCount = React.useMemo(() => {
    if (!BrandCitationValueCount) return [];

    if (isOnlyBusinessCompetitor) {
      return BrandCitationValueCount.filter(
        (item) => item.is_business_competitor || item.is_own_brand,
      );
    }

    return BrandCitationValueCount; // Return all items when not filtering
  }, [BrandCitationValueCount, isOnlyBusinessCompetitor]);

  // 6. Brand Mention Value Count - A list of json {brand, count}
  const BrandMentionValueCount = React.useMemo(() => {
    if (!filteredQueryResearchData) return [];
    const brandCounts = {};
    filteredQueryResearchData.data.llm_responses.forEach((response) => {
      response.mention_stats.forEach((mention) => {
        const brandName = mention.brand_name;
        if (brandCounts[brandName]) {
          brandCounts[brandName].count += mention.mention_count;
        } else {
          brandCounts[brandName] = {
            brand: brandName,
            count: mention.mention_count,
            is_own_brand: mention.is_own_brand,
            is_business_competitor: mention.is_business_competitor,
          };
        }
      });
    });
    return Object.values(brandCounts).sort((a, b) => b.count - a.count);
  }, [filteredQueryResearchData]);

  const filteredBrandMentionValueCount = React.useMemo(() => {
    if (!BrandMentionValueCount) return [];

    if (isOnlyBusinessCompetitor) {
      return BrandMentionValueCount.filter(
        (item) => item.is_business_competitor || item.is_own_brand,
      );
    }

    return BrandMentionValueCount; // Return all items when not filtering
  }, [BrandMentionValueCount, isOnlyBusinessCompetitor]);

  const URLCitationValueCount = React.useMemo(() => {
    if (!filteredQueryResearchData) return [];
    const urlCounts = {};

    filteredQueryResearchData.data.llm_responses.forEach((response) => {
      response.citation_stats.forEach((citation) => {
        citation.url_stats.forEach((urlStat) => {
          const rawUrl = urlStat.url;
          const displayUrl =
            rawUrl.length > 99 ? rawUrl.substring(0, 99) + "..." : rawUrl;

          if (urlCounts[rawUrl]) {
            urlCounts[rawUrl].count += urlStat.url_count;
          } else {
            urlCounts[rawUrl] = {
              url: displayUrl, // Just the string, no JSX
              link: (
                <a
                  href={rawUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-decoration-none"
                >
                  <SquareArrowOutUpRight
                    size={18}
                    style={{ color: "#49999d" }}
                  />
                </a>
              ),
              count: urlStat.url_count,
              is_own_brand: citation.is_own_brand,
              is_business_competitor: citation.is_business_competitor,
              brand_name: citation.brand_name,
            };
          }
        });
      });
    });

    return Object.values(urlCounts).sort((a, b) => {
      if (b.count !== a.count) {
        return b.count - a.count;
      }
      return (b.is_own_brand ? 1 : 0) - (a.is_own_brand ? 1 : 0);
    });
  }, [filteredQueryResearchData]);

  const filteredURLCitationValueCount = React.useMemo(() => {
    if (!URLCitationValueCount) return [];

    if (isOwnBrandOnly) {
      return URLCitationValueCount.filter((item) => item.is_own_brand);
    }

    return URLCitationValueCount; // Return all items when not filtering
  }, [URLCitationValueCount, isOwnBrandOnly]);

  // 8. All Brand Mention Snippets (modified to show all mentions)
  const allBrandMentionSnippets = React.useMemo(() => {
    return (
      filteredQueryResearchData?.data?.llm_responses
        ?.reduce((snippets, response) => {
          // Get all mention_stats (not filtered by own brand)
          const allMentions = response.mention_stats.filter(
            (mentionStat) =>
              mentionStat.is_business_competitor || mentionStat.is_own_brand,
          );

          // Extract all snippets from all mentions
          allMentions
            .reduce((allSnippetsMap, mentionStat) => {
              const snippets = mentionStat.snippets || [];
              snippets.forEach((snippet) => {
                if (!allSnippetsMap.has(snippet.text)) {
                  allSnippetsMap.set(snippet.text, {
                    mentions: new Map(),
                    text: snippet.text,
                  });
                }

                const snippetData = allSnippetsMap.get(snippet.text);
                const mentionText = snippet.text.substring(
                  snippet.start_index,
                  snippet.end_index,
                );

                // Store each mention with its brand type
                snippetData.mentions.set(mentionText, {
                  text: mentionText,
                  is_own_brand: mentionStat.is_own_brand,
                });
              });
              return allSnippetsMap;
            }, new Map())
            .forEach((snippetData) => {
              snippets.push({
                text: snippetData.text,
                mentions: Array.from(snippetData.mentions.values()),
              });
            });

          return snippets;
        }, [])
        ?.map((snippet, index) => {
          // Highlight all brand name mentions
          const highlightedText = snippet.mentions.reduce((text, mention) => {
            const className = mention.is_own_brand
              ? "own-brand-mention"
              : "competitor-mention";
            return text.replace(
              mention.text,
              `<span class="${className}">${mention.text}</span>`,
            );
          }, snippet.text);

          return {
            id: index + 1,
            text: (
              <div className="d-flex align-items-start justify-content-between">
                <span
                  dangerouslySetInnerHTML={{ __html: highlightedText }}
                  className="flex-grow-1 me-2"
                />
                <button
                  type="button"
                  className="btn btn-sm p-1 eye-button"
                  onClick={() => openModal(snippet.text)}
                  title="View full LLM response"
                >
                  <Eye size={16} />
                </button>
              </div>
            ),
            is_own_brand: snippet.mentions.some((m) => m.is_own_brand),
            originalText: snippet.text,
          };
        }) || []
    );
  }, [filteredQueryResearchData, openModal]);

  const filteredAllBrandMentionSnippets = React.useMemo(() => {
    if (!allBrandMentionSnippets) return [];

    if (isOwnBrandMentionsOnly) {
      return allBrandMentionSnippets.filter((item) => item.is_own_brand);
    }

    return allBrandMentionSnippets; // Return all items when not filtering
  }, [allBrandMentionSnippets, isOwnBrandMentionsOnly]);

  return (
    <div>
      {/* ScoreCards */}
      <div className="row">
        <div className="col-md-3">
          <ScoreCard
            title={t("dashboard.totalCitations", "Total Citations")}
            value={totalClientCitation}
            icon="bi bi-link-45deg"
            isLoaded={true}
          />
        </div>
        <div className="col-md-3">
          <ScoreCard
            title={t("dashboard.brandCitationShare", "Brand Citation Share")}
            value={shareOfCitations + "%"}
            icon="bi bi-clock-history"
            isLoaded={true}
          />
        </div>
        <div className="col-md-3">
          <ScoreCard
            title={t("dashboard.totalBrandMentions", "Total Brand Mentions")}
            value={totalClientMention}
            icon="bi bi-globe2"
            isLoaded={true}
          />
        </div>
        <div className="col-md-3">
          <ScoreCard
            title={t("dashboard.brandMentionShare", "Brand Mention Share")}
            value={shareOfMentions + "%"}
            icon="bi bi-people"
            isLoaded={true}
          />
        </div>
      </div>
      <br />
      <h4 className="h5 fw-semi-bold text-dark">
        {t("promptResearch.shareOfVoiceInsights", "Share of Voice Insights")}
      </h4>
      <div className="row align-items-stretch">
        <div className="col-md-6 d-flex flex-column" style={{ gap: "0.75rem" }}>
          <ToggleInput
            label={t(
              "dashboard.onlyBusinessCompetitors",
              "Business Competitors Only",
            )}
            checked={isOnlyBusinessCompetitor}
            onChange={(e) => setIsOnlyBusinessCompetitor(e.target.checked)}
          />
          <DonutChart
            data={filteredBrandCitationValueCount}
            title={t("promptResearch.shareOfCitations", "Share of Citations")}
            keyColumn="brand_name"
            valueColumn="count"
            pinColumn={"is_own_brand"}
            isLoaded={true}
            height={250}
            truncateLegend={false}
            noDataMessage={t(
              "promptResearch.noUrlsCited",
              "No URLs were cited in the LLM responses.",
            )}
            className="flex-fill"
          />
        </div>
        <div className="col-md-6 d-flex flex-column" style={{ gap: "0.75rem" }}>
          <ToggleInput
            label={t("promptResearch.ownBrandOnly", "Own Brand Only")}
            checked={isOwnBrandOnly}
            onChange={(e) => setIsOwnBrandOnly(e.target.checked)}
          />
          <ThreeColumnTable
            data={filteredURLCitationValueCount}
            title={t("promptResearch.topCitedPages", "Top Cited Pages")}
            column1Key="url"
            column2Key="link"
            column3Key="count"
            column1Title={t("promptResearch.page", "Page")}
            column2Title=""
            column3Title={t("promptResearch.citations", "Citations")}
            columnRatio="78-5-15"
            columnAlignment="l-r-r"
            isLoaded={true}
            boxHeight={300}
            noDataMessage={
              isOwnBrandOnly
                ? t(
                    "promptResearch.noOwnUrlsCited",
                    "None of your brand's URLs were cited in the LLM responses.",
                  )
                : t(
                    "promptResearch.noUrlsCited",
                    "No URLs were cited in the LLM responses.",
                  )
            }
            className="flex-fill"
          />
        </div>
      </div>
      <br />
      <div className="row">
        <div className="col-md-6">
          <div className="d-flex justify-content-end">
            {/* Non-interactive spacer: removed control from accessibility tree */}
            <div
              className="form-check form-switch d-flex align-items-center"
              aria-hidden="true"
              role="presentation"
              style={{ visibility: "hidden", margin: 0 }}
            />
          </div>
          <br />

          <DonutChart
            data={filteredBrandMentionValueCount}
            keyColumn="brand"
            valueColumn="count"
            pinColumn={"is_own_brand"}
            title={t("promptResearch.shareOfMentions", "Share of Mentions")}
            isLoaded={true}
            height={250}
            truncateLegend={false}
            noDataMessage={t(
              "promptResearch.noBrandsMentioned",
              "No brand names were mentioned in the LLM responses.",
            )}
          />
        </div>
        <div className="col-md-6">
          <ToggleInput
            label={t(
              "promptResearch.showOwnMentionsOnly",
              "Show Own Mentions Only",
            )}
            checked={isOwnBrandMentionsOnly}
            onChange={(e) => setIsOwnBrandMentionsOnly(e.target.checked)}
          />
          <br />
          <SingleColumnTable
            title={t("promptResearch.allMentions", "All Mentions")}
            data={filteredAllBrandMentionSnippets}
            exportData={filteredAllBrandMentionSnippets.map((item) => ({
              text: item.originalText,
            }))}
            keyColumn="text"
            isLoaded={true}
            boxHeight={300}
            noDataMessage={
              isOwnBrandMentionsOnly
                ? t(
                    "promptResearch.noOwnBrandMentioned",
                    "Your brand was not mentioned in the LLM responses.",
                  )
                : t(
                    "promptResearch.noBrandOrCompetitorMentioned",
                    "Neither your brand nor your competitors were mentioned in the LLM responses.",
                  )
            }
          />
        </div>
      </div>

      {/* Modal Component */}
      <LLMResponseModal
        showModal={showModal}
        modalContent={modalContent}
        onClose={closeModal}
      />
    </div>
  );
}

export default QueryResearchResult;
